import React from 'react';
import { PageHeader, SegmentedControl } from '@parsina/framework';

import { useTranslation } from '../../i18n';
import useDocumentTitle from '../../lib/useDocumentTitle';
import { useUrlState } from '../../lib/urlState';
import ProfilesView from './ProfilesView';
import InsurersView from './InsurersView';

export const VIEWS = ['profiles', 'insurers'];

/** The URL contract (DESIGN_CONCEPT §3.2). Defaults are absent from the address. */
const SCHEMA = {
  view: { default: 'profiles', values: VIEWS },
  // The insurer catalogue shown: an ISO key (IR, IQ …); '' = every row. Junk
  // is dropped rather than sent (the BFF would refuse it).
  catalog: { default: '', parse: (raw) => (/^[A-Za-z]{2}$/.test(raw) ? raw.toUpperCase() : null) },
  inactive: { default: '', values: ['', '1'] },
};

/**
 * «کشورها» — the rules of each jurisdiction (PM-0218,
 * docs/country-config/CONTRACT.md). Archetype B (Report/List):
 *
 *   ① PageHeader with the two views (`semantics="tabs"`) · ② the deployment
 *   alert · ⑤ the table.
 *
 * «پروفایل‌ها» lists the stored rows (`*` first); a row opens the country's
 * record (`/countries/:country`), where it is read and edited. «بیمه‌گرها» is
 * accounting's platform-owned insurer catalogue by country, read-only — what a
 * profile's `insuranceCatalog` key holds.
 */
export default function Countries() {
  const { t } = useTranslation();
  useDocumentTitle('nav.title.countries');
  const [view, setView] = useUrlState(SCHEMA);

  const changeView = (next) => {
    if (next !== view.view) setView({ view: next, catalog: '', inactive: '' });
  };

  return (
    <div className="flex h-full flex-col bg-background" data-testid="countries">
      <PageHeader title={t('countries.title')} sub={t('countries.sub')}>
        <SegmentedControl
          semantics="tabs"
          size="sm"
          ariaLabel={t('countries.views.label')}
          options={VIEWS.map((value) => ({ value, label: t(`countries.views.${value}`) }))}
          value={view.view}
          onChange={changeView}
          data-testid="countries-tabs"
        />
      </PageHeader>

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        <div
          role="tabpanel"
          aria-label={t(`countries.views.${view.view}`)}
          className="mx-auto w-full max-w-[1440px]"
          data-testid={`countries-panel-${view.view}`}
        >
          {view.view === 'insurers'
            ? <InsurersView view={view} onChange={setView} />
            : <ProfilesView />}
        </div>
      </div>
    </div>
  );
}
